import { useEffect, useRef, useState } from "react";
import { ArrowLeft, Loader2, SendHorizontal, Users } from "lucide-react";

import { cn, initialsOf } from "@/lib/utils";
import { ApiError } from "@/lib/apiClient";
import { Avatar } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import {
  formatAmount,
  messageClock,
  useGroupMessages,
  useSendGroupMessage,
  type GroupMessage,
  type GroupRoom,
} from "../api/groupChat";

/** Group-room chat pane: room header, multi-party message list, composer. Visually
 *  mirrors ChatThread, but bubbles show the sender's name (the room is multi-party) and
 *  "mine" bubbles are matched by senderId === myId. Messages poll (see useGroupMessages). */
export function GroupChatThread({
  room,
  myId,
  onBack,
  className,
}: {
  room: GroupRoom;
  myId: number | null;
  onBack?: () => void;
  className?: string;
}) {
  const [draft, setDraft] = useState("");
  // Set when the server rejects a send because the user hasn't opted in (403).
  const [optInRequired, setOptInRequired] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);
  const taRef = useRef<HTMLTextAreaElement>(null);
  const formRef = useRef<HTMLFormElement>(null);

  const sendMessage = useSendGroupMessage();
  const { data: messages, isLoading } = useGroupMessages(room.loanId);

  // Reset composer state when the selected room changes.
  useEffect(() => {
    setDraft("");
    setOptInRequired(false);
  }, [room.loanId]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: "end" });
  }, [messages]);

  // Auto-grow the composer with its content (capped, then it scrolls).
  useEffect(() => {
    const ta = taRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = `${Math.min(ta.scrollHeight, 160)}px`;
  }, [draft]);

  const sending = sendMessage.isPending;

  const WORD_LIMIT = 500;
  const wordCount = draft.trim() ? draft.trim().split(/\s+/).length : 0;
  const tooLong = wordCount > WORD_LIMIT;

  const send = async (e: React.FormEvent) => {
    e.preventDefault();
    const body = draft.trim();
    if (!body || sending || tooLong) return;
    try {
      await sendMessage.mutateAsync({ loanId: room.loanId, body });
      setDraft("");
      setOptInRequired(false);
    } catch (err) {
      // Member without opt-in → server returns 403 { reason: "optin-required" }.
      if (
        err instanceof ApiError &&
        err.status === 403 &&
        (err.body as { reason?: string } | undefined)?.reason === "optin-required"
      ) {
        setOptInRequired(true);
      }
      // Keep the draft so the user can retry; a toast layer can surface other errors later.
    }
  };

  return (
    <section className={cn("min-w-0 flex-1 flex-col", className)}>
      {/* Header */}
      <header className="flex items-center gap-3 border-b border-white/5 px-4 py-3 md:px-6">
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            aria-label="Tilbake til grupperom"
            className="-ml-1 rounded-full p-1.5 text-white/60 transition-colors hover:bg-white/5 hover:text-white md:hidden"
          >
            <ArrowLeft className="size-5" />
          </button>
        )}
        <Avatar initials={initialsOf(room.title)} />
        <div className="min-w-0 flex-1">
          <div className="truncate font-semibold text-white">{room.title}</div>
          <div className="flex items-center gap-1.5 text-xs text-white/50">
            <Users className="size-3.5 shrink-0 text-teal-300/70" />
            <span>
              {room.optInMemberCount} {room.optInMemberCount === 1 ? "deltaker" : "deltakere"}
            </span>
            {room.amount != null && (
              <>
                <span className="text-white/25">·</span>
                <span>{formatAmount(room.amount)}</span>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Messages */}
      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-4 md:px-6">
        {isLoading ? (
          <MessagesSkeleton />
        ) : !messages?.length ? (
          <p className="pt-10 text-center text-sm text-white/40">
            Ingen meldinger ennå. Send den første meldingen i dette grupperommet.
          </p>
        ) : (
          <>
            <div className="flex justify-center">
              <span className="rounded-full bg-white/5 px-3 py-1 text-xs text-white/50">
                Meldinger
              </span>
            </div>
            {messages.map((m) => (
              <MessageBubble key={m.id} message={m} mine={m.senderId === myId} />
            ))}
          </>
        )}
        <div ref={endRef} />
      </div>

      {/* Composer */}
      {optInRequired ? (
        <div className="border-t border-white/5 px-4 py-4 text-center text-sm text-white/40">
          Du må skru på gruppe-chat i Innstillinger for å skrive i grupperommet.
        </div>
      ) : (
        <form ref={formRef} onSubmit={send} className="border-t border-white/5 px-4 py-3">
          <div className="flex items-end gap-2">
            <textarea
              ref={taRef}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                // Enter sends; Shift+Enter inserts a newline.
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  formRef.current?.requestSubmit();
                }
              }}
              rows={1}
              placeholder="Skriv en melding …"
              aria-label="Skriv en melding"
              className="max-h-40 min-h-[44px] flex-1 resize-none rounded-2xl border border-white/10 bg-white/5 px-4 py-2.5 text-sm leading-relaxed text-white placeholder:text-white/40 focus:border-teal-400/50 focus:outline-none"
            />
            <button
              type="submit"
              aria-label="Send melding"
              disabled={!draft.trim() || sending || tooLong}
              className="flex size-11 shrink-0 items-center justify-center rounded-full bg-teal-400 text-[#04231d] transition-opacity hover:bg-teal-300 disabled:opacity-40"
            >
              {sending ? (
                <Loader2 className="size-5 animate-spin" />
              ) : (
                <SendHorizontal className="size-5" />
              )}
            </button>
          </div>
          {wordCount >= WORD_LIMIT * 0.8 && (
            <p
              className={cn(
                "mt-1.5 px-1 text-right text-xs tabular-nums",
                tooLong ? "text-red-300" : "text-white/40"
              )}
            >
              {wordCount} / {WORD_LIMIT} ord{tooLong ? " · maks 500" : ""}
            </p>
          )}
        </form>
      )}
    </section>
  );
}

function MessagesSkeleton() {
  const rows = [
    { mine: false, w: "w-48" },
    { mine: true, w: "w-32" },
    { mine: false, w: "w-56" },
    { mine: true, w: "w-40" },
  ];
  return (
    <>
      {rows.map((r, i) => (
        <div key={i} className={cn("flex flex-col", r.mine ? "items-end" : "items-start")}>
          <Skeleton
            className={cn("h-10 rounded-2xl", r.w, r.mine ? "rounded-br-md" : "rounded-bl-md")}
          />
          <Skeleton className="mt-1 h-2.5 w-8" />
        </div>
      ))}
    </>
  );
}

function MessageBubble({ message, mine }: { message: GroupMessage; mine: boolean }) {
  return (
    <div className={cn("flex flex-col", mine ? "items-end" : "items-start")}>
      {/* Multi-party room → show who sent it (not on my own bubbles). */}
      {!mine && (
        <span className="mb-0.5 px-1 text-xs font-medium text-teal-200/80">
          {message.senderName ?? "Ukjent avsender"}
        </span>
      )}
      <div
        className={cn(
          "max-w-[85%] whitespace-pre-wrap break-words rounded-2xl px-4 py-2.5 text-sm shadow-sm md:max-w-[72%]",
          mine
            ? "rounded-br-md bg-teal-500 text-[#04231d]"
            : "rounded-bl-md bg-white/[0.14] text-white ring-1 ring-inset ring-white/10"
        )}
      >
        {message.body}
      </div>
      <span className="mt-1 px-1 text-[11px] text-white/35">
        {messageClock(message.createdAt)}
      </span>
    </div>
  );
}
